/**
 * Shopify CDN image URL utilities.
 *
 * Shopify's image CDN supports query-string transforms (`width`, `height`,
 * `crop`, `quality`, `format`) that resize and re-encode images on the fly.
 * Routing rendering through Shopify's CDN avoids paying for Vercel's image
 * optimization on assets Shopify is happy to serve.
 *
 * These helpers are pure URL builders. Render components decide whether to
 * use them (host-gated) so non-Shopify URLs flow through normal `next/image`.
 */

const SHOPIFY_HOSTS = ['cdn.shopify.com'];
const SHOPIFY_HOST_SUFFIXES = ['.myshopify.com'];

/**
 * Widths the wrapper hands to Shopify when generating responsive `srcSet`
 * variants. Mirrors the device sizes we use for `next/image` so layout
 * decisions (e.g. `sizes` props) stay valid after the migration.
 */
export const SHOPIFY_RESPONSIVE_WIDTHS = [
	320, 420, 640, 750, 828, 1080, 1200, 1440, 1920, 2400,
] as const;

/**
 * Default DPR multipliers Shopify will satisfy for fixed-size images.
 * Used when callers provide an intrinsic width without `sizes`.
 */
export const SHOPIFY_FIXED_WIDTH_MULTIPLIERS = [1, 2] as const;

const DEFAULT_QUALITY = 75;

/** File extensions where Shopify resize parameters should be skipped. */
const SKIP_TRANSFORM_EXTENSIONS = new Set(['svg', 'gif']);

export interface ShopifyImageTransform {
	width?: number;
	height?: number;
	/** Shopify accepts `center`, `top`, `bottom`, `left`, `right`. */
	crop?: 'center' | 'top' | 'bottom' | 'left' | 'right';
	/** 1-100. Defaults to 75 to mirror Next.js. */
	quality?: number;
}

/** True when the URL points at the Shopify-hosted CDN. */
export function isShopifyImageUrl(src: string): boolean {
	if (!src) return false;
	let url: URL;
	try {
		url = new URL(src);
	} catch {
		return false;
	}
	const host = url.hostname.toLowerCase();
	if (SHOPIFY_HOSTS.includes(host)) return true;
	return SHOPIFY_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

function getExtension(pathname: string): string | null {
	const lastSegment = pathname.split('/').pop() ?? '';
	const dot = lastSegment.lastIndexOf('.');
	if (dot < 0 || dot === lastSegment.length - 1) return null;
	return lastSegment.slice(dot + 1).toLowerCase();
}

function shouldSkipTransform(url: URL): boolean {
	const ext = getExtension(url.pathname);
	if (!ext) return false;
	return SKIP_TRANSFORM_EXTENSIONS.has(ext);
}

/**
 * Apply Shopify CDN resize parameters to a Shopify-hosted image URL.
 *
 * - Returns the original `src` unchanged for non-Shopify URLs, malformed URLs,
 *   or formats we should not transform (SVG, GIF).
 * - Preserves any pre-existing query parameters.
 * - Width is rounded to the nearest integer Shopify will accept.
 */
export function buildShopifyImageUrl(src: string, transform: ShopifyImageTransform = {}): string {
	if (!isShopifyImageUrl(src)) return src;

	let url: URL;
	try {
		url = new URL(src);
	} catch {
		return src;
	}

	if (shouldSkipTransform(url)) return src;

	if (transform.width !== undefined) {
		url.searchParams.set('width', String(Math.round(transform.width)));
	}
	if (transform.height !== undefined) {
		url.searchParams.set('height', String(Math.round(transform.height)));
	}
	if (transform.crop) {
		url.searchParams.set('crop', transform.crop);
	}
	if (transform.quality !== undefined) {
		url.searchParams.set('quality', String(Math.max(1, Math.min(100, transform.quality))));
	} else if (!url.searchParams.has('quality')) {
		url.searchParams.set('quality', String(DEFAULT_QUALITY));
	}

	return url.toString();
}

interface ResponsiveSrcSetOptions {
	/** Optional aspect ratio (height / width) used to derive a paired height. */
	aspectRatio?: number;
	/** Shopify crop mode when both width and height are provided. */
	crop?: ShopifyImageTransform['crop'];
	/** 1-100. */
	quality?: number;
	/** Override the responsive width list (largest variant included verbatim). */
	widths?: readonly number[];
	/** Cap variants at this width to avoid oversizing. */
	maxWidth?: number;
}

/**
 * Build a responsive `srcSet` string of width descriptors for a Shopify URL.
 *
 * Returns an empty string for non-Shopify URLs so callers can fall back to a
 * default behavior without branching.
 */
export function buildShopifyResponsiveSrcSet(
	src: string,
	options: ResponsiveSrcSetOptions = {},
): string {
	if (!isShopifyImageUrl(src)) return '';

	const widthsSource = options.widths ?? SHOPIFY_RESPONSIVE_WIDTHS;
	const cap = options.maxWidth;
	const widths = (cap ? widthsSource.filter((w) => w <= cap) : [...widthsSource]).sort(
		(a, b) => a - b,
	);
	if (widths.length === 0) return '';

	return widths
		.map((width) => {
			const transform: ShopifyImageTransform = {
				width,
				quality: options.quality,
			};
			if (options.aspectRatio) {
				transform.height = Math.round(width * options.aspectRatio);
				transform.crop = options.crop ?? 'center';
			}
			return `${buildShopifyImageUrl(src, transform)} ${width}w`;
		})
		.join(', ');
}

interface FixedSrcSetOptions {
	height?: number;
	crop?: ShopifyImageTransform['crop'];
	quality?: number;
	multipliers?: readonly number[];
}

/**
 * Build a DPR-style `srcSet` for a Shopify image with a known intrinsic
 * width (e.g. fixed thumbnails). Returns an empty string for non-Shopify URLs.
 */
export function buildShopifyFixedSrcSet(
	src: string,
	width: number,
	options: FixedSrcSetOptions = {},
): string {
	if (!isShopifyImageUrl(src)) return '';
	if (!width || width <= 0) return '';

	const multipliers = options.multipliers ?? SHOPIFY_FIXED_WIDTH_MULTIPLIERS;

	return multipliers
		.map((multiplier) => {
			const transform: ShopifyImageTransform = {
				width: width * multiplier,
				quality: options.quality,
			};
			if (options.height !== undefined) {
				transform.height = options.height * multiplier;
				transform.crop = options.crop ?? 'center';
			}
			return `${buildShopifyImageUrl(src, transform)} ${multiplier}x`;
		})
		.join(', ');
}

/**
 * Pick a single Shopify-sized URL appropriate for HTML preloading and similar
 * one-shot fetches. Falls back to the original URL for non-Shopify hosts.
 */
export function buildShopifyPreloadUrl(src: string, transform: ShopifyImageTransform = {}): string {
	return buildShopifyImageUrl(src, transform);
}
